/**
 * In-app languages, no library: one flat JSON file per language under
 * ./locales, English is the source of truth, every other file may only hold
 * keys English has (src/i18n/i18n.test.ts).
 *
 *   t("fan.menu.share")                       -> "Share DJ profile"
 *   t("requests.count", { count: 3 })         -> uses "requests.count_one" / "_other"
 *   t("cashout.min", { amount: "€5" })        -> fills "{amount}"
 *
 * A missing translation falls back to English, never to an error. Only the
 * viewer's language file is downloaded (English ships in the main bundle).
 *
 * Changing language saves the choice and reloads the app (the way Spotify
 * restarts), so plain `t()` calls anywhere (components, utils) always read
 * the current language. No hook, no provider.
 */
import en from "./locales/en.json";
import { Lang, READY, pickLanguage } from "./languages";

type Dict = Record<string, string>;
type PluralForm = "zero" | "one" | "two" | "few" | "many" | "other";
type Base<K> = K extends `${infer B}_${PluralForm}` ? B : K;
export type MessageKey = Base<keyof typeof en>;

const EN: Dict = en;
const STORAGE_KEY = "requestapp.lang";

/**
 * The languages this build offers: every READY language, on the website and
 * (from 1.5.2) in the iPhone / Android app — the 1.5.1 app was English-only
 * (founder, 2026-10-08). The app bundle declares the same languages
 * (ios Info.plist CFBundleLocalizations, android res/xml/locales_config.xml)
 * so the phone reports its language to the web view.
 */
export const offeredLanguages = (): readonly Lang[] => READY;

const loaders: Record<Exclude<Lang, "en">, () => Promise<{ default: Dict }>> = {
  fr: () => import("./locales/fr.json"),
  es: () => import("./locales/es.json"),
  de: () => import("./locales/de.json"),
  it: () => import("./locales/it.json"),
  ja: () => import("./locales/ja.json"),
  el: () => import("./locales/el.json"),
  pt: () => import("./locales/pt.json"),
  nl: () => import("./locales/nl.json"),
};

let current: Lang = "en";
let dict: Dict = EN;

export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  let k: string = key;
  if (typeof vars?.count === "number") {
    const plural = `${key}_${new Intl.PluralRules(current).select(vars.count)}`;
    k = plural in dict || plural in EN ? plural : `${key}_other` in EN ? `${key}_other` : key;
  }
  return fill(dict[k] ?? EN[k] ?? k, vars);
}

/** Put values into "{name}" slots; an unknown slot stays visible rather than blank. */
export function fill(text: string, vars?: Record<string, string | number>): string {
  return vars ? text.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m)) : text;
}

export const currentLanguage = (): Lang => current;

/**
 * Locale for dates and numbers. English keeps the phone's own formats, as it
 * always has; every other language formats in that language ("lun. 14 oct.").
 */
export const formatLocale = (): string | undefined => (current === "en" ? undefined : current);

const phoneLanguages = (): readonly string[] =>
  typeof navigator === "undefined" ? [] : navigator.languages?.length ? navigator.languages : [navigator.language];

/** The language this viewer should see right now (saved choice, then phone, then English). */
export function detectLanguage(): Lang {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* storage blocked: follow the phone */
  }
  return pickLanguage(saved, phoneLanguages(), offeredLanguages());
}

/**
 * The offered language the phone / browser itself asks for, or null when it
 * speaks none of them (a Polish phone): that viewer follows the phone into
 * English, and the picker says "Automatic" instead of naming a language.
 */
export function phoneLanguage(): Lang | null {
  const lang = pickLanguage(null, phoneLanguages(), offeredLanguages());
  return lang !== "en" || phoneLanguages().some((tag) => /^en([-_]|$)/i.test(tag)) ? lang : null;
}

/**
 * The boot spinner waits on the language file (src/index.tsx). A download
 * that stalls (venue Wi-Fi) held it up to the 2-minute chunk timeout; after
 * this long the app starts in English instead, and a file that turns up later
 * is ignored, so the language never switches mid-visit. The saved choice still
 * applies on the next open (2026-10-10).
 */
const LANGUAGE_LOAD_TIMEOUT_MS = 3000;

/** Load a language's file and make it current. A failed or stalled download keeps English. */
export async function loadLanguage(lang: Lang): Promise<Lang> {
  if (lang === "en" || !offeredLanguages().includes(lang)) {
    current = "en";
    dict = EN;
  } else {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stalled = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), LANGUAGE_LOAD_TIMEOUT_MS);
    });
    try {
      const file = await Promise.race([loaders[lang](), stalled]);
      current = file ? lang : "en";
      dict = file ? file.default : EN;
    } catch {
      current = "en";
      dict = EN;
    } finally {
      clearTimeout(timer);
    }
  }
  document.documentElement.lang = current;
  return current;
}

/** The language this viewer picked in Settings, or null when it follows the phone. */
export function savedLanguageChoice(): Lang | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && offeredLanguages().includes(saved as Lang) ? (saved as Lang) : null;
  } catch {
    return null;
  }
}

/** The viewer picked a language in Settings: remember it on this device. `null` = follow the phone again. */
export function saveLanguageChoice(lang: Lang | null) {
  try {
    if (lang) localStorage.setItem(STORAGE_KEY, lang);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked: the choice lasts until the app closes */
  }
}
